<template>
  <div class="lesson" :style="accentVars">
    <!-- barra superior -->
    <div class="lesson__topbar">
      <button class="tlink" type="button" @click="$emit('back')">
        <i class="mdi mdi-arrow-left"></i> {{ $t("lesson.backToLessons") }}
      </button>
      <button
        v-if="classLink"
        class="tlink tlink--cta"
        type="button"
        @click="openLink"
      >
        <i class="mdi mdi-open-in-new"></i> {{ $t("lesson.openClassLink") }}
      </button>
    </div>

    <!-- cabecalho da aula -->
    <header class="hero">
      <p class="hero__eyebrow">
        <span class="hero__dot"></span>
        <span class="hero__cat">{{ categoryText }}</span> ·
        {{ $t("lesson.lessonWord") }}
        <!-- Card 86ak2ncuv: pré-ementa e opcional são os "box" marcados pelo
             administrativo ao subir a aula. O aluno precisa saber se a aula
             faz parte do programa ou é complemento. -->
        <span v-if="lesson.isPre" class="hero__tag">{{
          $t("course.preSyllabus")
        }}</span>
        <span v-if="lesson.isOptional" class="hero__tag hero__tag--soft">{{
          $t("course.optional")
        }}</span>
      </p>
      <div class="hero__row">
        <h1 class="hero__title">{{ lesson.title || $t("course.untitled") }}</h1>
        <div class="hero__meta">
          <span class="pill pill--count">
            <i class="mdi mdi-layers-triple-outline"></i>
            {{
              $tc("course.materials", materialsCount, {
                count: materialsCount,
              })
            }}
          </span>
          <!-- Sem professor, quem fecha a aula é o próprio aluno. Com
               professor, o selo é só leitura: a chamada é dele. -->
          <button
            v-if="canComplete"
            type="button"
            class="pill pill--action"
            :class="{ 'pill--done': completed }"
            :disabled="completing"
            @click="$emit('toggle-complete', !completed)"
          >
            <i
              class="mdi"
              :class="completed ? 'mdi-check-circle' : 'mdi-circle-outline'"
            ></i>
            {{
              completed ? $t("course.completedTag") : $t("lesson.markCompleted")
            }}
          </button>
          <span v-else-if="completed" class="pill pill--done">
            <i class="mdi mdi-check-circle"></i> {{ $t("course.completedTag") }}
          </span>
        </div>
      </div>
      <p v-if="lesson.description" class="hero__desc">
        {{ lesson.description }}
      </p>
    </header>

    <!-- MATERIAIS: palco + filmstrip -->
    <div class="slabel">
      <span class="slabel__k">{{ $t("lesson.materials") }}</span>
      <span v-if="hasContent" class="slabel__hint">{{
        $t("lesson.navHint")
      }}</span>
    </div>

    <template v-if="hasContent">
      <div ref="stage" class="stage" :class="{ 'stage--expanded': expanded }">
        <div class="stage__chrome">
          <div class="stage__name">
            <i class="mdi" :class="activeIcon"></i>
            <strong>{{ activeMaterial.name || $t("lesson.material") }}</strong>
          </div>
          <span class="stage__spacer"></span>

          <!-- Zoom só no PDF: PPT e vídeo são embeds com controles próprios,
               que não dá para comandar de fora. No fallback nativo quem manda
               é a barra do próprio navegador. -->
          <div v-if="activeKind === 'pdf' && !pdfFallback" class="zoom">
            <button
              type="button"
              class="zoom__btn"
              :aria-label="$t('lesson.zoomOut')"
              :disabled="zoom <= ZOOM_MIN"
              @click="zoomOut"
            >
              <i class="mdi mdi-minus"></i>
            </button>
            <button
              type="button"
              class="zoom__level"
              :aria-label="$t('lesson.zoomReset')"
              @click="zoomReset"
            >
              {{ zoom }}%
            </button>
            <button
              type="button"
              class="zoom__btn"
              :aria-label="$t('lesson.zoomIn')"
              :disabled="zoom >= ZOOM_MAX"
              @click="zoomIn"
            >
              <i class="mdi mdi-plus"></i>
            </button>
          </div>

          <button
            type="button"
            class="zoom__btn"
            :aria-label="expanded ? $t('lesson.collapse') : $t('lesson.expand')"
            @click="toggleExpanded"
          >
            <i
              class="mdi"
              :class="expanded ? 'mdi-arrow-collapse' : 'mdi-arrow-expand'"
            ></i>
          </button>

          <span class="type-tag">{{ activeType }}</span>
          <span class="counter"
            >{{ pad2(activeIndex + 1) }}
            <small>/ {{ pad2(materialsCount) }}</small></span
          >
        </div>

        <div
          class="stage__view"
          :class="{ 'stage__view--dark': activeKind === 'video' }"
        >
          <div v-if="activeKind === 'pdf'" class="stage__fill">
            <PdfZoomViewer
              v-if="!pdfFallback"
              :key="activeMaterial.url"
              :url="activeMaterial.url"
              :zoom="zoom"
              @zoom-step="onZoomStep"
              @error="pdfFallback = true"
            />
            <!-- Se o pdf.js não conseguir ler o arquivo, o leitor nativo do
                 navegador assume, com a barra dele (e o zoom dele) visível. -->
            <iframe
              v-else
              :key="activeMaterial.url"
              :src="activeMaterial.url"
              width="100%"
              height="100%"
              frameborder="0"
              allowfullscreen
            ></iframe>
          </div>

          <div v-else-if="activeKind === 'ppt'" class="stage__fill">
            <DocumentViewerIframe :file-url="activeMaterial.url" />
          </div>

          <div
            v-else-if="activeKind === 'video'"
            class="stage__fill stage__center"
          >
            <!-- Sem largura fixa: com o palco maior, travar em 820px deixava
                 o vídeo pequeno no meio de uma caixa grande. -->
            <video
              :key="activeMaterial.url"
              width="100%"
              controls
              class="stage__video"
            >
              <source :src="activeMaterial.url" type="video/mp4" />
              {{ $t("common.videoUnsupported") }}
            </video>
          </div>

          <div v-else class="stage__center stage__unknown">
            <i class="mdi mdi-file-question-outline"></i>
            <span>{{ $t("lesson.noPreview") }}</span>
          </div>
        </div>
      </div>

      <div class="strip">
        <button
          class="nav"
          type="button"
          :aria-label="$t('lesson.prevMaterial')"
          @click="prev"
        >
          <i class="mdi mdi-chevron-left"></i>
        </button>
        <div class="strip__scroll">
          <button
            v-for="(item, i) in lesson.content"
            :key="item.id || i"
            ref="frames"
            class="frame"
            :class="{ 'frame--on': i === activeIndex }"
            type="button"
            @click="go(i)"
          >
            <span class="frame__ic"
              ><i class="mdi" :class="iconFor(item)"></i
            ></span>
            <span class="frame__n">{{ pad2(i + 1) }}</span>
            <span class="frame__t">{{ typeLabel(item) }}</span>
          </button>
        </div>
        <button
          class="nav"
          type="button"
          :aria-label="$t('lesson.nextMaterial')"
          @click="next"
        >
          <i class="mdi mdi-chevron-right"></i>
        </button>
      </div>
    </template>

    <div v-else class="empty">
      <i class="mdi mdi-tray-arrow-down"></i>
      <p>{{ $t("lesson.noMaterials") }}</p>
    </div>

    <!-- VOCABULARIO -->
    <div class="slabel">
      <span class="slabel__k">{{ $t("lesson.vocabulary") }}</span>
      <span v-if="hasKeywords" class="slabel__hint">{{
        $tc("lesson.keywords", lesson.keywords.length, {
          count: lesson.keywords.length,
        })
      }}</span>
    </div>

    <div v-if="hasKeywords" class="vocab">
      <div v-for="(word, i) in lesson.keywords" :key="i" class="word">
        <div class="word__term">
          <i class="mdi mdi-bookmark"></i>{{ word.keyword }}
        </div>
        <p v-if="word.description" class="word__def">{{ word.description }}</p>
      </div>
    </div>
    <p v-else class="muted-note">
      {{ $t("lesson.noKeywords") }}
    </p>

    <!-- ANOTACOES (slot; aluno e professor preenchem, com textos diferentes) -->
    <template v-if="$slots.notes">
      <div v-if="notesLabel" class="slabel">
        <span class="slabel__k">{{ $t("lesson.notes") }}</span>
        <span class="slabel__hint">{{
          notesHint || $t("lesson.notesHint")
        }}</span>
      </div>
      <slot name="notes"></slot>
    </template>
  </div>
</template>

<script>
import DocumentViewerIframe from "@/components/DocumentViewerIframe.vue";
import PdfZoomViewer from "@/components/connect/course/PdfZoomViewer.vue";
import { categoryColor, categoryLabel } from "@/shared/courseTiers";

export default {
  name: "LessonContent",
  components: { DocumentViewerIframe, PdfZoomViewer },
  props: {
    // Aula (CourseClasses): title, description, category, content[], keywords[]
    lesson: { type: Object, required: true },
    completed: { type: Boolean, default: false },
    // Aluno sem professor pode fechar a própria aula (a API rejeita os demais).
    canComplete: { type: Boolean, default: false },
    completing: { type: Boolean, default: false },
    classLink: { type: String, default: "" },
    // Pré-seleciona um material pelo id (deep-link vindo do dashboard/agenda)
    initialMaterialId: { type: String, default: "" },
    // Texto do cabeçalho do mural: muda entre aluno e professor
    notesHint: { type: String, default: "" },
    // false: a pagina desenha os proprios cabecalhos (aula do aluno, com os
    // blocos que abrem e fecham do retorno de 16/10).
    notesLabel: { type: Boolean, default: true },
  },
  data() {
    return {
      activeIndex: 0,
      // Card 86aknb8n7: zoom do PDF desenhado pelo PdfZoomViewer (pdf.js);
      // 100% = página na largura do palco.
      zoom: 100,
      ZOOM_MIN: 50,
      ZOOM_MAX: 300,
      ZOOM_STEP: 25,
      // O pdf.js não conseguiu abrir o arquivo: usa o leitor do navegador.
      pdfFallback: false,
      expanded: false,
    };
  },
  computed: {
    accent() {
      return categoryColor(this.lesson.category);
    },
    accentVars() {
      const c = this.accent;
      return {
        "--accent": c,
        "--accent-soft": this.rgba(c, 0.1),
        "--accent-shadow": this.rgba(c, 0.42),
      };
    },
    categoryText() {
      return categoryLabel(this.lesson.category);
    },
    hasContent() {
      return (
        Array.isArray(this.lesson.content) && this.lesson.content.length > 0
      );
    },
    hasKeywords() {
      return (
        Array.isArray(this.lesson.keywords) && this.lesson.keywords.length > 0
      );
    },
    materialsCount() {
      return this.hasContent ? this.lesson.content.length : 0;
    },
    activeMaterial() {
      return this.hasContent ? this.lesson.content[this.activeIndex] || {} : {};
    },
    activeKind() {
      return this.kindOf(this.activeMaterial);
    },
    activeType() {
      return this.typeLabel(this.activeMaterial);
    },
    activeIcon() {
      return this.iconFor(this.activeMaterial);
    },
  },
  created() {
    this.selectInitial();
  },
  mounted() {
    window.addEventListener("keydown", this.onKey);
  },
  beforeDestroy() {
    window.removeEventListener("keydown", this.onKey);
    // Sair da aula com o material expandido nao pode deixar a pagina travada.
    this.lockPageScroll(false);
  },
  methods: {
    pad2(n) {
      return String(n).padStart(2, "0");
    },
    rgba(hex, alpha) {
      const h = hex.replace("#", "");
      const r = parseInt(h.substring(0, 2), 16);
      const g = parseInt(h.substring(2, 4), 16);
      const b = parseInt(h.substring(4, 6), 16);
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    },
    selectInitial() {
      if (!this.hasContent) return;
      if (this.initialMaterialId) {
        const i = this.lesson.content.findIndex(
          (m) => String(m.id) === String(this.initialMaterialId)
        );
        this.activeIndex = i >= 0 ? i : 0;
      } else {
        this.activeIndex = 0;
      }
    },
    go(i) {
      this.activeIndex = i;
      // Zoom é do material que está aberto: trocar de material recomeça em
      // 100%, senão o próximo PDF abre num zoom que ninguém pediu.
      this.zoom = 100;
      this.pdfFallback = false;
      this.scrollFrameIntoView(i);
    },
    // Ctrl+roda / pinça dentro do PDF
    onZoomStep(direction) {
      if (direction > 0) this.zoomIn();
      else this.zoomOut();
    },
    zoomIn() {
      this.zoom = Math.min(this.ZOOM_MAX, this.zoom + this.ZOOM_STEP);
    },
    zoomOut() {
      this.zoom = Math.max(this.ZOOM_MIN, this.zoom - this.ZOOM_STEP);
    },
    zoomReset() {
      this.zoom = 100;
    },
    // Expandir cobre a viewport inteira mantendo a barra do palco (e, com
    // ela, o zoom e a navegação). O scroll da página fica travado enquanto
    // isso, senão a página corre atrás do material.
    toggleExpanded() {
      this.expanded = !this.expanded;
      this.lockPageScroll(this.expanded);
    },
    lockPageScroll(locked) {
      if (typeof document === "undefined") return;
      document.body.style.overflow = locked ? "hidden" : "";
    },
    prev() {
      this.go(
        (this.activeIndex - 1 + this.materialsCount) % this.materialsCount
      );
    },
    next() {
      this.go((this.activeIndex + 1) % this.materialsCount);
    },
    scrollFrameIntoView(i) {
      this.$nextTick(() => {
        const el = this.$refs.frames && this.$refs.frames[i];
        if (el && el.scrollIntoView) {
          el.scrollIntoView({ block: "nearest", inline: "center" });
        }
      });
    },
    onKey(e) {
      if (!this.hasContent) return;
      // Nao navega enquanto o aluno digita uma anotacao
      const tag = (e.target.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || e.target.isContentEditable) {
        return;
      }
      if (e.key === "Escape" && this.expanded) {
        this.toggleExpanded();
        return;
      }
      if (e.key === "ArrowLeft") this.prev();
      if (e.key === "ArrowRight") this.next();
    },
    openLink() {
      if (this.classLink) window.open(this.classLink, "_blank");
    },
    isPDF(type) {
      return String(type || "").includes("pdf");
    },
    isVideo(type) {
      return String(type || "").includes("mp4");
    },
    isPPT(type) {
      const t = String(type || "");
      return (
        t.includes("presentation") ||
        t.includes("ppt") ||
        t.includes("pptx") ||
        t.includes("powerpoint")
      );
    },
    kindOf(item) {
      const type = item && item.type;
      if (this.isPDF(type)) return "pdf";
      if (this.isPPT(type)) return "ppt";
      if (this.isVideo(type)) return "video";
      return "other";
    },
    iconFor(item) {
      const map = {
        pdf: "mdi-file-pdf-box",
        ppt: "mdi-file-presentation-box",
        video: "mdi-play-circle",
        other: "mdi-file-document-outline",
      };
      return map[this.kindOf(item)];
    },
    typeLabel(item) {
      const map = {
        pdf: "PDF",
        ppt: this.$t("lesson.typeSlides"),
        video: this.$t("lesson.typeVideo"),
        other: this.$t("lesson.material"),
      };
      return map[this.kindOf(item)];
    },
  },
  watch: {
    "lesson.content"() {
      this.selectInitial();
    },
  },
};
</script>

<style scoped>
.lesson {
  --ink: var(--dk-ink, #211b2e);
  --ink-soft: var(--dk-ink-soft, #6e6a79);
  --ink-faint: var(--dk-faint, #9a96a6);
  --line: var(--dk-line, #eae7f0);
  --line-strong: var(--dk-line-strong, #ded9e8);
  --surface: var(--dk-surface, #ffffff);
  --accent: var(--dk-grape, #9923cd);
  --accent-soft: rgba(153, 35, 205, 0.1);
  --accent-shadow: rgba(153, 35, 205, 0.42);

  max-width: 1120px;
  margin: 0 auto;
  padding: clamp(18px, 3.5vw, 36px) clamp(4px, 2vw, 16px) 72px;
  font-family: "Poppins", sans-serif;
  color: var(--ink);
}

/* topbar */
.lesson__topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 26px;
}
.tlink {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  border: 0;
  background: transparent;
  font-family: "Poppins", sans-serif;
  font-size: 13.5px;
  font-weight: 500;
  color: var(--ink-soft);
  cursor: pointer;
  padding: 6px 2px;
  transition: color 0.18s ease;
}
.tlink:hover {
  color: var(--ink);
}
.tlink:focus-visible {
  outline: 2px solid var(--accent);
  outline-offset: 2px;
  border-radius: 6px;
}
.tlink .mdi {
  font-size: 18px;
}
.tlink--cta {
  border: 1px solid var(--line-strong);
  border-radius: 999px;
  padding: 10px 18px;
  font-weight: 600;
  color: var(--ink);
}
.tlink--cta:hover {
  border-color: var(--accent);
  color: var(--accent);
  box-shadow: 0 10px 22px -14px var(--accent-shadow);
}

/* hero */
.hero {
  padding-bottom: 24px;
  margin-bottom: 8px;
  border-bottom: 1px solid var(--line);
}
.hero__eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 9px;
  margin: 0 0 12px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: var(--ink-faint);
}
.hero__dot {
  width: 10px;
  height: 10px;
  border-radius: 3px;
  background: var(--accent);
}
.hero__cat {
  color: var(--accent);
}
.hero__tag {
  display: inline-block;
  margin-left: 8px;
  padding: 2px 9px;
  border: 1px solid var(--accent);
  border-radius: 999px;
  color: var(--accent);
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: none;
  vertical-align: middle;
}
.hero__tag--soft {
  border-color: #c8c2d1;
  color: var(--dk-muted, #6b6472);
}
.hero__row {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
}
.hero__title {
  margin: 0;
  font-family: "Poppins", sans-serif;
  font-weight: 700;
  font-size: clamp(1.9rem, 4.4vw, 2.9rem);
  line-height: 1;
  letter-spacing: -0.015em;
  color: var(--ink);
  overflow-wrap: anywhere;
}
.hero__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 13px;
  border-radius: 999px;
  font-size: 12.5px;
  font-weight: 600;
  white-space: nowrap;
}
.pill--count {
  background: var(--dk-surface, #fff);
  border: 1px solid var(--line-strong);
  color: var(--ink-soft);
}
.pill--count .mdi {
  color: var(--accent);
  font-size: 15px;
}
.pill--done {
  background: #e9f7f0;
  color: var(--dk-teal-deep, #087a55);
}
.pill--done .mdi {
  font-size: 15px;
}
.pill--action {
  border: 1px solid var(--line-strong);
  background: var(--dk-surface, #fff);
  color: var(--ink-soft);
  cursor: pointer;
  font-family: inherit;
  transition: border-color 0.18s ease, color 0.18s ease;
}
.pill--action:hover:not(:disabled) {
  border-color: var(--accent);
  color: var(--accent);
}
.pill--action:focus-visible {
  outline: 2px solid var(--accent);
  outline-offset: 2px;
}
.pill--action:disabled {
  opacity: 0.55;
  cursor: progress;
}
.pill--action.pill--done {
  border-color: transparent;
  color: var(--dk-teal-deep, #087a55);
}
.pill--action .mdi {
  font-size: 15px;
}
.hero__desc {
  margin: 16px 0 0;
  max-width: 68ch;
  font-size: 14.5px;
  line-height: 1.65;
  color: var(--ink-soft);
}

/* section label */
.slabel {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 10px;
  margin: 40px 0 18px;
}
.slabel__k {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--ink-faint);
}
.slabel__hint {
  font-size: 12.5px;
  color: var(--ink-faint);
}

/* ===== STAGE ===== */
.stage {
  border: 1px solid var(--line);
  border-radius: 20px;
  overflow: hidden;
  background: var(--surface);
  box-shadow: 0 30px 60px -44px rgba(33, 27, 46, 0.4);
}
.stage__chrome {
  display: flex;
  align-items: center;
  /* Zoom e expandir entraram aqui; em tela estreita a barra quebra em vez de
     espremer o nome do material. */
  flex-wrap: wrap;
  gap: 12px;
  padding: 13px 18px;
  border-bottom: 1px solid var(--line);
  background: var(--dk-surface, #fff);
}
.stage__name {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}
.stage__name .mdi {
  color: var(--accent);
  font-size: 20px;
  flex: none;
}
.stage__name strong {
  font-weight: 600;
  font-size: 14px;
  color: var(--ink);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.stage__spacer {
  flex: 1 1 auto;
}
.type-tag {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 11px;
  border-radius: 8px;
  background: var(--accent-soft);
  color: var(--accent);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  white-space: nowrap;
}
.counter {
  font-family: "Poppins", sans-serif;
  font-weight: 700;
  font-size: 1.25rem;
  letter-spacing: -0.01em;
  color: var(--ink);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.counter small {
  color: var(--ink-faint);
  font-weight: 600;
}

/* Card 86aknb8n7: a caixa era min(64vh, 620px) e sobrava tela nos dois
   eixos. O teto maior atende o pedido sem precisar expandir toda vez. */
.stage__view {
  position: relative;
  height: min(82vh, 900px);
  background: #f3f0f7;
}

/* Expandido: o palco cobre a viewport mantendo a própria barra -- é lá que
   ficam o zoom e a navegação entre materiais. */
.stage--expanded {
  position: fixed;
  inset: 0;
  z-index: 200;
  border-radius: 0;
  border: 0;
  display: flex;
  flex-direction: column;
  box-shadow: none;
}
.stage--expanded .stage__view {
  flex: 1;
  height: auto;
}

.zoom {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 2px;
  border: 1px solid var(--line);
  border-radius: 999px;
}
.zoom__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: var(--ink);
  cursor: pointer;
}
.zoom__btn:hover:not(:disabled) {
  background: var(--accent-soft);
  color: var(--accent);
}
.zoom__btn:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}
.zoom__level {
  min-width: 52px;
  padding: 0 6px;
  border: 0;
  background: transparent;
  color: var(--ink-soft);
  font-size: 0.78rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  cursor: pointer;
}
.zoom__level:hover {
  color: var(--accent);
}
.stage__view--dark {
  background: #0f0b14;
}
.stage__fill {
  position: absolute;
  inset: 0;
  height: 100%;
  width: 100%;
}
.stage__fill iframe {
  height: 100%;
  width: 100%;
  border: 0;
}
.stage__center {
  display: flex;
  align-items: center;
  justify-content: center;
}
.stage__video {
  max-width: 100%;
  max-height: 100%;
}
.stage__unknown {
  position: absolute;
  inset: 0;
  flex-direction: column;
  gap: 10px;
  color: var(--ink-faint);
  font-size: 13.5px;
}
.stage__unknown .mdi {
  font-size: 40px;
}

/* filmstrip */
.strip {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 16px;
}
.strip__scroll {
  display: flex;
  gap: 10px;
  overflow-x: auto;
  padding: 6px 2px;
  flex: 1 1 auto;
  scrollbar-width: thin;
}
.frame {
  flex: none;
  width: 92px;
  border: 1px solid var(--line);
  border-radius: 14px;
  background: var(--dk-surface, #fff);
  padding: 12px 10px 10px;
  cursor: pointer;
  text-align: center;
  transition: transform 0.18s ease, box-shadow 0.18s ease,
    border-color 0.18s ease, background 0.18s ease;
}
.frame:hover {
  transform: translateY(-3px);
  border-color: var(--line-strong);
  box-shadow: 0 16px 30px -22px rgba(33, 27, 46, 0.5);
}
.frame:focus-visible {
  outline: 2px solid var(--accent);
  outline-offset: 2px;
}
.frame__ic {
  display: block;
  font-size: 22px;
  color: var(--ink-faint);
  line-height: 1;
}
.frame__n {
  display: block;
  font-family: "Poppins", sans-serif;
  font-weight: 700;
  font-size: 1.5rem;
  line-height: 1;
  margin: 6px 0 4px;
  color: var(--ink);
  font-variant-numeric: tabular-nums;
}
.frame__t {
  display: block;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--ink-faint);
}
.frame--on {
  background: var(--accent);
  border-color: var(--accent);
  transform: translateY(-4px);
  box-shadow: 0 20px 34px -20px var(--accent-shadow);
}
.frame--on .frame__ic,
.frame--on .frame__n,
.frame--on .frame__t {
  color: #fff;
}
.nav {
  flex: none;
  width: 42px;
  height: 42px;
  border-radius: 50%;
  border: 1px solid var(--line-strong);
  background: var(--dk-surface, #fff);
  color: var(--ink);
  display: grid;
  place-items: center;
  cursor: pointer;
  transition: border-color 0.18s ease, color 0.18s ease, box-shadow 0.18s ease;
}
.nav:hover {
  border-color: var(--accent);
  color: var(--accent);
  box-shadow: 0 12px 22px -14px var(--accent-shadow);
}
.nav:focus-visible {
  outline: 2px solid var(--accent);
  outline-offset: 2px;
}
.nav .mdi {
  font-size: 22px;
}

/* empty */
.empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 56px 24px;
  border: 1px dashed var(--line-strong);
  border-radius: 20px;
  text-align: center;
  color: var(--ink-soft);
  font-size: 14px;
}
.empty .mdi {
  font-size: 36px;
  color: var(--dk-line-strong, #c9c4d4);
}

/* vocabulary */
.vocab {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(230px, 1fr));
  gap: 12px;
}
.word {
  border: 1px solid var(--line);
  border-radius: 14px;
  background: var(--dk-surface, #fff);
  padding: 16px 18px;
  transition: transform 0.18s ease, box-shadow 0.18s ease,
    border-color 0.18s ease;
}
.word:hover {
  transform: translateY(-3px);
  border-color: var(--line-strong);
  box-shadow: 0 18px 32px -26px rgba(33, 27, 46, 0.5);
}
.word__term {
  display: flex;
  align-items: baseline;
  gap: 8px;
  font-family: "Poppins", sans-serif;
  font-weight: 700;
  font-size: 1.15rem;
  color: var(--accent);
  letter-spacing: -0.01em;
  overflow-wrap: anywhere;
}
.word__term .mdi {
  font-size: 15px;
  color: var(--accent);
  opacity: 0.6;
  flex: none;
}
.word__def {
  margin: 8px 0 0;
  font-size: 13px;
  line-height: 1.55;
  color: var(--ink-soft);
}
.muted-note {
  margin: 0;
  padding: 18px 20px;
  border: 1px dashed var(--line-strong);
  border-radius: 14px;
  font-size: 13.5px;
  color: var(--ink-soft);
}

@media (max-width: 760px) {
  .hero__row {
    align-items: flex-start;
  }
  /* No celular a tela ja e a caixa: 82vh empurraria a trilha de materiais
     para fora da primeira dobra. */
  .stage__view {
    height: min(62vh, 520px);
  }
  .stage--expanded .stage__view {
    height: auto;
  }
}
@media (prefers-reduced-motion: reduce) {
  .tlink,
  .tlink--cta,
  .frame,
  .nav,
  .word {
    transition: none;
  }
  .frame:hover,
  .frame--on,
  .word:hover {
    transform: none;
  }
}
</style>
